/**
 * Persist the website weekly report softcopy so email can attach the same PDF.
 * Collection: weekly_reports/{meterId}_{YYYY-MM-DD}
 */
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import type { WeeklyReportPayload } from './buildWeeklyReportPayload';
import type { RenderedHealthForPdf, RenderedSectionForPdf } from '@/components/dashboard/WeeklyReportPdfDocument';

export const WEEKLY_REPORTS_COLLECTION = 'weekly_reports';

export interface SavedWeeklyReport {
  meterId: string;
  weekStart: string;
  payload: WeeklyReportPayload;
  sections: RenderedSectionForPdf[];
  health: RenderedHealthForPdf[];
  generatedAt: string;
}

export function weekStartYmd(weekStart: Date): string {
  return weekStart.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

export function weeklyReportDocId(meterId: string, weekStart: Date | string): string {
  const ymd = typeof weekStart === 'string' ? weekStart.slice(0, 10) : weekStartYmd(weekStart);
  return `${meterId}_${ymd}`;
}

/** Firestore rejects `undefined` in nested fields — strip before setDoc. */
function stripUndefined<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export async function loadSavedWeeklyReport(
  meterId: string,
  weekStart: Date,
): Promise<SavedWeeklyReport | null> {
  const snap = await getDoc(doc(db, WEEKLY_REPORTS_COLLECTION, weeklyReportDocId(meterId, weekStart)));
  if (!snap.exists()) return null;
  const data = snap.data() as SavedWeeklyReport;
  if (!data?.payload || !Array.isArray(data.sections)) return null;
  return data;
}

export async function saveWeeklyReport(entry: {
  meterId: string;
  weekStart: Date;
  payload: WeeklyReportPayload;
  sections: RenderedSectionForPdf[];
  health: RenderedHealthForPdf[];
}): Promise<void> {
  const weekStart = weekStartYmd(entry.weekStart);
  await setDoc(
    doc(db, WEEKLY_REPORTS_COLLECTION, weeklyReportDocId(entry.meterId, weekStart)),
    stripUndefined({
      meterId: entry.meterId,
      weekStart,
      payload: entry.payload,
      sections: entry.sections,
      health: entry.health,
      generatedAt: entry.payload.generatedAt || new Date().toISOString(),
    } satisfies SavedWeeklyReport),
  );
}
